import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container } from "react-bootstrap";
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Header from "./components/Header";
import Footer from "./components/Footer";
import Showcase from "./components/Showcase";
import Navigation from "./components/Navigation";
import useScrollToHash from "./functions/useScrollToHash";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLoadQuizGroups } from "./hooks/useLoadQuizGroups";
import { useLoadQuizChallenges } from "./hooks/useLoadQuizChallenges";
import { useLoadQuizList } from "./hooks/useLoadQuizList";
import { useLoadGeneralFeedback } from "./hooks/useLoadGeneralFeedback";
import { useLoadQuestionFeedback } from "./hooks/useLoadQuestionFeedback";
import { useHandleGroupUpdate } from "./hooks/useHandleGroupUpdate";
import { useHandleLiveGame } from "./hooks/useHandleLiveGame";
import { tokenExpired } from "./utils/checkToken";
import { useQuizLogoutMutation } from "./slices/fun_stuff/quizUsersSlice";
import { quizLogout } from "./slices/fun_stuff/quizAuthSlice";

const App = () => {
  useHandleGroupUpdate();
  useHandleLiveGame();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [quizApiLogout] = useQuizLogoutMutation();
  
  const { quizUserInfo }= useSelector((state) => state.quizAuth || {});
  const hasFetchedRef = useRef(false);
  const hasFetchedTokenRef = useRef(false);

  const { loadGroups } = useLoadQuizGroups();
  const { loadChallenges } = useLoadQuizChallenges();
  const { loadQuizzes } = useLoadQuizList();
  const { loadGeneralFeedback } = useLoadGeneralFeedback();
  const { loadQuestionFeedback }= useLoadQuestionFeedback();

  useEffect(() => {
    if (!quizUserInfo || hasFetchedTokenRef.current) return;

    const initAuth = async () => {
      hasFetchedTokenRef.current = true;

      if (!quizUserInfo?.token|| tokenExpired(quizUserInfo.token)) {
        console.log('No current token found, logging out')
        window.alert('Your session has expired, please login again')
        await quizApiLogout().unwrap();
        dispatch(quizLogout());
        navigate('/quizgame');
        return;
      }
    };

    initAuth();
  }, [quizUserInfo, quizApiLogout, dispatch, navigate]);


  useEffect(() => {
    if (!quizUserInfo || hasFetchedRef.current) return;

    const fetchAll = async () => {
      try {
        await Promise.all([
            loadGroups(),
            loadChallenges(),
            loadQuizzes(),
        ]);

        if (quizUserInfo.is_admin) {
          await Promise.all([
            loadGeneralFeedback(),
            loadQuestionFeedback(),
          ]);
        }
      } catch (error) {
        console.error("Error preloading data in App.js:", error);
      } finally {
        hasFetchedRef.current = true;
      }
    };

    fetchAll();
  }, [quizUserInfo, loadGroups, loadChallenges, loadQuizzes, loadGeneralFeedback, loadQuestionFeedback]);

  const location = useLocation();
  const pathname = location.pathname;

  let showcaseVariant = 'default';
  if (pathname.startsWith('/quiz')) {
    showcaseVariant = 'quiz';
  } else if (pathname.startsWith('/homestead')) {
    showcaseVariant = 'homestead';
  }

  useScrollToHash();

  return (
    <>
      <Header />
      <Navigation />
      <Showcase variant={showcaseVariant} />
      <main>
        <Container>
          <Outlet />
        </Container>
      </main>
      <Footer />
      <ToastContainer />
    </>
  )
}

export default App
